import { memo, useEffect, useRef, useState } from "react";
import type { CSSProperties, FC, KeyboardEvent, PointerEvent, RefObject } from "react";
import { Link } from "@tanstack/react-router";
import { Reading } from "@/components/plumb/Reading";
import { EFFECTS } from "@/components/plumb/effects";
import { FOUNDER, HERO, LENSES, depthOf } from "./content";
import type { Lens } from "./content";
import { pad, reducedMotion } from "./hooks";
import { GRADES, PixelType } from "./PixelType";

const clamp = (v: number, a: number, b: number) => Math.min(b, Math.max(a, v));

/** One window onto the band: the same picture, at the same place, as one moving reading. */
const Frame: FC<{ reading: number }> = ({ reading }) => {
  const effect = EFFECTS[reading % EFFECTS.length];
  return (
    <>
      <div className="sd-lens-in">
        <Reading className="sd-cover" src={HERO.src} depth={depthOf(HERO.src)} effect={effect} />
      </div>
      <span className="sd-lens-tag">{effect}</span>
    </>
  );
};

/** A drifting window. Drag it across the picture; press it to switch to the next reading. */
const Window: FC<{ lens: Lens }> = memo(({ lens }) => {
  const [pos, setPos] = useState({ x: lens.x, y: lens.y });
  const [reading, setReading] = useState(lens.reading);
  const [held, setHeld] = useState(false);
  const drag = useRef<{ px: number; py: number; x: number; y: number; moved: boolean } | null>(null);

  const down = (e: PointerEvent<HTMLDivElement>) => {
    e.currentTarget.setPointerCapture(e.pointerId);
    drag.current = { px: e.clientX, py: e.clientY, x: pos.x, y: pos.y, moved: false };
    setHeld(true);
  };
  const move = (e: PointerEvent<HTMLDivElement>) => {
    const d = drag.current;
    const band = e.currentTarget.parentElement;
    if (!d || !band) return;
    const dx = e.clientX - d.px;
    const dy = e.clientY - d.py;
    if (Math.abs(dx) + Math.abs(dy) > 4) d.moved = true;
    const box = band.getBoundingClientRect();
    setPos({
      x: clamp(d.x + dx / box.width, -lens.w / 2, 1 - lens.w / 2),
      y: clamp(d.y + dy / box.height, -lens.h / 2, 1 - lens.h / 2),
    });
  };
  const up = () => {
    const d = drag.current;
    drag.current = null;
    setHeld(false);
    if (d && !d.moved) setReading(n => n + 1);
  };
  const key = (e: KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      setReading(n => n + 1);
    }
  };

  return (
    <div
      className="sd-lens"
      role="button"
      tabIndex={0}
      aria-label={`${EFFECTS[reading % EFFECTS.length]} reading. Press to switch.`}
      data-drag={held || undefined}
      style={
        {
          "--fx": pos.x,
          "--fy": pos.y,
          "--fw": lens.w,
          "--fh": lens.h,
          "--dx": lens.dx,
          "--dy": lens.dy,
          "--t": `${lens.seconds}s`,
        } as CSSProperties
      }
      onPointerDown={down}
      onPointerMove={move}
      onPointerUp={up}
      onPointerCancel={up}
      onKeyDown={key}
    >
      <Frame reading={reading} />
    </div>
  );
});
Window.displayName = "Window";

/** Size of the window that follows the pointer, as fractions of the band. */
const LOUPE = { w: 0.13, h: 0.4 };

/** Height of the bar the hero pins under. */
const NAV = 32;

/**
 * How far the pinned hero has been scrolled through, as a step from 0 to
 * `steps`. The hero holds still for that stretch (it pins under the bar, or by
 * its bottom edge when it is taller than the window), and the page only moves
 * on once the last step is reached.
 */
function useScrub(wrap: RefObject<HTMLElement | null>, pin: RefObject<HTMLElement | null>, steps: number): number {
  const [step, setStep] = useState(steps);

  useEffect(() => {
    const outer = wrap.current;
    const inner = pin.current;
    if (!outer || !inner || reducedMotion()) return;
    let raf = 0;
    const read = () => {
      raf = 0;
      const top = Math.min(NAV, window.innerHeight - inner.offsetHeight);
      outer.style.setProperty("--pin-top", `${top}px`);
      const travel = outer.offsetHeight - inner.offsetHeight;
      const done = travel > 0 ? clamp((top - outer.getBoundingClientRect().top) / travel, 0, 1) : 1;
      setStep(Math.min(steps, Math.floor(done * (steps + 1))));
    };
    const queue = () => {
      if (!raf) raf = requestAnimationFrame(read);
    };
    read();
    window.addEventListener("scroll", queue, { passive: true });
    window.addEventListener("resize", queue);
    const watch = new ResizeObserver(queue);
    watch.observe(inner);
    return () => {
      window.removeEventListener("scroll", queue);
      window.removeEventListener("resize", queue);
      watch.disconnect();
      if (raf) cancelAnimationFrame(raf);
    };
  }, [wrap, pin, steps]);

  return step;
}

/**
 * The picture as a still band with windows over it; the readings inside the
 * windows are the part that moves. Each window shows how the machine
 * reads what is underneath: they drift, they can be dragged, and one follows
 * the pointer. Pressing a window, or the picture, switches the reading. Under
 * it the title starts in its coarsest pixel grade and sharpens as you scroll.
 */
export const Hero: FC = () => {
  const [at, setAt] = useState({ x: 0, y: 0 });
  const [near, setNear] = useState(false);
  const [reading, setReading] = useState(HERO.loupe);
  const wrap = useRef<HTMLElement>(null);
  const pin = useRef<HTMLDivElement>(null);
  const grade = useScrub(wrap, pin, GRADES.length);

  const track = (e: PointerEvent<HTMLDivElement>) => {
    const onWindow = e.target instanceof Element && e.target.closest(".sd-lens");
    setNear(e.pointerType === "mouse" && !onWindow);
    if (e.pointerType !== "mouse" || onWindow) return;
    const box = e.currentTarget.getBoundingClientRect();
    setAt({ x: (e.clientX - box.left) / box.width - LOUPE.w / 2, y: (e.clientY - box.top) / box.height - LOUPE.h / 2 });
  };
  const press = (e: PointerEvent<HTMLDivElement>) => {
    if (e.target instanceof Element && !e.target.closest(".sd-lens")) setReading(n => n + 1);
  };

  return (
    <header ref={wrap} id="top" className="sd-hero">
      <div ref={pin} className="sd-hero-pin">
        <div className="sd-band" onPointerMove={track} onPointerLeave={() => setNear(false)} onPointerDown={press}>
          <img className="sd-cover" src={HERO.src} alt="" />
          {LENSES.map(lens => (
            <Window key={lens.id} lens={lens} />
          ))}
          {/* always mounted, so the reading is ready the moment the pointer arrives */}
          <div
            className="sd-lens sd-lens-loupe"
            hidden={!near}
            style={{ "--fx": at.x, "--fy": at.y, "--fw": LOUPE.w, "--fh": LOUPE.h } as CSSProperties}
          >
            <Frame reading={reading} />
          </div>
          <p className="sd-bench-tag">{HERO.hint}</p>
        </div>
        <div className="sd-title-row">
          <div className="sd-tagline">
            <p>{HERO.first}</p>
            <Link className="sd-more" to="/about">
              About us →
            </Link>
          </div>
          <div>
            <PixelType lines={HERO.title} grade={grade} />
            <p className="sd-gloss">{HERO.gloss}</p>
          </div>
        </div>
        <div className="sd-by">
          <p className="sd-label">
            <span>
              {HERO.by.company} founded by{" "}
              <a href={HERO.by.founderUrl} target="_blank" rel="noreferrer">
                {FOUNDER}
              </a>
            </span>
            <span>{HERO.by.place}</span>
          </p>
          <p>
            {grade < GRADES.length
              ? `scroll to sharpen / pixel grade ${pad(grade + 1)} of ${pad(GRADES.length)}`
              : "scroll to read"}
          </p>
          <span aria-hidden>↓</span>
        </div>
      </div>
      <div className="sd-hero-scrub" />
    </header>
  );
};
